import { Breadcrumbs } from "@mui/material";
import { useMatches, useLocation } from "react-router-dom";
import { useMemo, useCallback } from "react";
import { sphereColors } from "@styles/common-colors";
import { SdbProject } from "@custom-types/project-types";
import { useAppSelector } from "@store/store-helper";
import { getProjectByIdSelector } from "@store/projects/projects-selector";
import { RouteParams } from "@router/route-params";
import { useAppParams } from "@router/router-helper";
import { BreadcrumbHandle } from "@utils/faro-breadcrumb-utils";
import { sidebarSelector } from "@store/ui/ui-selector";
import { getGroupByIdSelector } from "@store/groups/groups-selector";
import { GroupTypes } from "@custom-types/group-types";
import { SdbMemberTypes } from "@custom-types/member-types";
import { companyMemberSelector } from "@store/members/members-selector";
import { currentUserSelector } from "@store/user/user-selector";
import { BreadcrumbEvents } from "@utils/track-event/track-event-list";
import { SphereDashboardAPITypes } from "@stellar/api-logic";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { getTeamByIdSelector } from "@store/teams/teams-selector";
import { selectedCompanyContextSelector } from "@store/sdb-company/sdb-company-selector";
import { BreadcrumbItem, NO_TRANSLATE_CLASS } from "@faro-lotv/flat-ui";
import { FaroCoreApiTypes } from "@stellar/core-api-client";

/** Extended definition when using useMatches to provide types to the attributes that we know. */
interface MatchObject<HandleT> extends ReturnType<typeof useMatches> {
  /** id of the route, supplied by react-router */
  id: string;
  /** the path of the matched route, defined by us in the routes */
  pathname: string;
  /** route params, defined in the routes */
  params: Partial<RouteParams>;
  /** the data object, which contains data returned by the loader of the route */
  data: unknown;
  /** a handle that includes the breadcrumb for the route  as the defined by the type BreadcrumbHandle */
  handle: HandleT;
}

/** Defines the data included to the breadcrumb handle. */
export interface BreadcrumbHandleData {
  /**
   * Includes the selected project object if applicable to the route, null otherwise.
   */
  selectedProject: SdbProject | null;

  /**
   * Includes the selected group object if applicable to the route.
   */
  selectedGroup?: GroupTypes;

  /**
   * Includes the selected company member if applicable to the route
   */
  selectedMember?: SdbMemberTypes | null;

  /**
   * Includes the selected team if applicable to the route
   */
  selectedTeam?: SphereDashboardAPITypes.ITeam | null;

  /**
   * Includes the current user.
   */
  currentUser?: SphereDashboardAPITypes.ICompanyMemberBase | null;

  /**
   * Includes the data coming in the route from useLoaderData
   *
   * @see https://reactrouter.com/en/main/hooks/use-loader-data
   */
  loaderData?: unknown;

  /** Get the query params for the breadcrumbs */
  queryParams?: string;

  /** List of features available in company context */
  companyContext?: FaroCoreApiTypes.GetCompanyContext | null;
}

export type Breadcrumb = {
  /** if this is set, the breadcrumb would be a clickable router-link */
  link?: string;

  /** the text of the breadcrumb */
  text: string;

  /** If this property is present the text should not be translated with localize */
  shouldNotTranslate?: true;

  /** Optional property to disable the breadcrumb */
  isDisabled?: boolean;
};

/** A dynamic breadcrumbs component that renders a collapsed breadcrumbs using the handles provided in the routes */
export function FaroBreadcrumbs(): JSX.Element {
  const matches = useMatches() as Array<MatchObject<BreadcrumbHandle>>;
  const location = useLocation();
  const queryParams = location.search;

  const { trackEvent } = useTrackEvent();
  const { projectId, groupId, memberId, teamId } = useAppParams();

  const selectedProject = useAppSelector(getProjectByIdSelector(projectId));
  const selectedGroup = useAppSelector(getGroupByIdSelector(groupId));
  const selectedMember = useAppSelector(companyMemberSelector(memberId));
  const selectedTeam = useAppSelector(getTeamByIdSelector(teamId));
  const currentUser = useAppSelector(currentUserSelector);
  const sidebar = useAppSelector(sidebarSelector);
  const companyContext = useAppSelector(selectedCompanyContextSelector);

  /** Size in pixels for the breadcrumb wrapper element. */
  const breadcrumbsMaxSize = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      sidebar.isFullSize ? 378 : 220,
    [sidebar.isFullSize]
  );

  const breadcrumbs = useMemo(() => {
    return (
      matches
        // get rid of any matches that don't have handle and breadcrumb
        .filter((match) => Boolean(match.handle?.breadcrumb))
        // get the breadcrumbs
        .map((match) =>
          typeof match.handle.breadcrumb === "function"
            ? match.handle.breadcrumb(match.params, {
                selectedProject,
                selectedGroup,
                selectedMember,
                selectedTeam,
                currentUser,
                loaderData: match.data,
                queryParams,
                companyContext,
              })
            : match.handle.breadcrumb
        )
        // flatten the array because the breadcrumb can be an array of breadcrumbs
        .flat()
        // get rid of any null or undefined breadcrumbs or breadcrumb.text
        .filter((match) => Boolean(match?.text)) as Breadcrumb[]
    );
  }, [
    matches,
    currentUser,
    selectedGroup,
    queryParams,
    selectedProject,
    selectedMember,
    selectedTeam,
    companyContext,
  ]);

  const handleBreadcrumbClick = useCallback(() => {
    trackEvent({
      name: BreadcrumbEvents.navigateWithBreadcrumb,
    });
  }, [trackEvent]);

  // Flag to show the full labels or shorten them with ellipses.
  const shouldShowFullLabels = false;
  const numberOfChildren = breadcrumbs.length;

  return (
    <Breadcrumbs
      aria-label="breadcrumb"
      sx={{
        // eslint-disable-next-line @typescript-eslint/naming-convention
        ">ol": {
          // Make the breadcrumbs stay on one line
          flexWrap: "nowrap",

          // Set the width of the parent container to be the minimum width,
          // so that the width of the children (li) will be calculated properly
          width: "min-content",

          // Add space between the separator and the labels
          gap: 2,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          ">li": {
            display: "flex",
            whiteSpace: "nowrap",

            // Set the items length as a percentage of the parent
            maxWidth: shouldShowFullLabels
              ? "100%"
              : `calc(100% / ${numberOfChildren})`,
          },
        },
        maxWidth: `calc(100vw - ${breadcrumbsMaxSize}px)`,
      }}
    >
      {breadcrumbs.map((item, i) => {
        const isLastItem = i === breadcrumbs.length - 1;

        return (
          <BreadcrumbItem
            key={i}
            label={item.text}
            sx={isLastItem ? { color: sphereColors.gray800 } : undefined}
            link={!isLastItem ? item.link : undefined}
            isDisabled={item.isDisabled}
            openInNewTab={false}
            onClickLink={handleBreadcrumbClick}
            className={item.shouldNotTranslate ? NO_TRANSLATE_CLASS : undefined}
            tooltipClasses={item.shouldNotTranslate ? { tooltip: NO_TRANSLATE_CLASS } : undefined}
          />
        );
      })}
    </Breadcrumbs>
  );
}
